/**
 * Shared binary-sensor state resolution.
 *
 * Device widgets (`BinarySensorWidget`) and room-level alarm aggregation
 * (`useRoomDeviceSignals`) must agree on which device value carries the
 * "sensor is active" signal and how its value is interpreted.
 */

/** Device value keys that can carry a binary sensor's primary state, in priority order. */
export const BINARY_SENSOR_STATE_KEYS = [
  'sensor_value',
  'contact',
  'occupancy',
  'motion',
  'presence',
  'detected',
  'open',
  'smoke',
  'water',
  'leak',
  'tamper',
  'vibration',
  'state',
  'power',
  'on_off',
] as const;

/**
 * Normalise the many shapes integrations emit for "active" into a boolean:
 * booleans, non-zero numbers, and the usual truthy string forms.
 */
export function isBinarySensorValueActive(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    return ['1', 'on', 'open', 'opened', 'active', 'detected', 'wet', 'alarm', 'true'].includes(normalized);
  }

  return false;
}

/** Raw device value currently carrying the sensor state (first key present, in priority order). */
export function findBinarySensorValue(
  deviceValues: Array<{ valueKey?: unknown; currentValue?: unknown }> | null | undefined,
): unknown {
  if (!deviceValues?.length) return undefined;

  for (const key of BINARY_SENSOR_STATE_KEYS) {
    const match = deviceValues.find((value) => String(value?.valueKey ?? '').toLowerCase() === key);
    if (match) return match.currentValue;
  }

  return undefined;
}

/** True when the device's primary binary-sensor value reads as active. */
export function resolveBinarySensorActiveFromValues(
  deviceValues: Array<{ valueKey?: unknown; currentValue?: unknown }> | null | undefined,
): boolean {
  return isBinarySensorValueActive(findBinarySensorValue(deviceValues));
}
